<template>
  <b-modal
    id="invoice-reconciliation-export-modal"
    ref="exportModal"
    title="Export Data"
    size="lg"
    @hidden="resetModal"
  >
    <b-form @submit.prevent="performExport">
      <b-row>
        <b-col md="6">
          <b-form-group label="Export Type *" label-for="export-type">
            <b-form-select
              id="export-type"
              v-model="exportData.type"
              :options="exportTypeOptions"
              required
            />
            <small v-if="error.type" class="text-danger">{{ error.type }}</small>
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group label="Format *" label-for="export-format">
            <b-form-select
              id="export-format"
              v-model="exportData.format"
              :options="formatOptions"
              required
            />
            <small v-if="error.format" class="text-danger">{{ error.format }}</small>
          </b-form-group>
        </b-col>
      </b-row>

      <b-row>
        <b-col md="6">
          <b-form-group label="Start Date">
            <flat-pickr
              class="form-control"
              v-model="exportData.startDate"
              :config="{ enableTime: false, dateFormat: 'Y-m-d' }"
            />
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group label="End Date">
            <flat-pickr
              class="form-control"
              v-model="exportData.endDate"
              :config="{ enableTime: false, dateFormat: 'Y-m-d' }"
            />
          </b-form-group>
        </b-col>
      </b-row>

      <b-row>
        <b-col md="6">
          <b-form-group label="Status">
            <v-select
              v-model="exportData.status"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              label="title"
              :options="statusOptions"
              placeholder="Select Status"
              :reduce="(option) => option.title"
              :clearable="true"
            />
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group label="Client">
            <v-select
              v-model="exportData.client"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              label="title"
              :options="clientOptions"
              placeholder="Select Client"
              :reduce="(option) => option.title"
              :clearable="true"
            />
          </b-form-group>
        </b-col>
      </b-row>

      <b-row>
        <b-col md="6">
          <b-form-group label="Category">
            <v-select
              v-model="exportData.category"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              label="title"
              :options="categoryOptions"
              placeholder="Select Category"
              :reduce="(option) => option.title"
              :clearable="true"
            />
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group label="Booking Status">
            <b-form-select
              v-model="exportData.bookingStatus"
              :options="bookingStatusOptions"
            />
          </b-form-group>
        </b-col>
      </b-row>

      <b-alert v-if="submitError" variant="danger" show>
        {{ submitError }}
      </b-alert>
    </b-form>

    <div slot="modal-footer">
      <b-button
        variant="primary"
        @click="performExport"
        :disabled="loading || !isFormValid"
      >
        <b-spinner v-if="loading" small class="mr-1" />
        Export
      </b-button>
      <b-button variant="secondary" @click="$refs.exportModal.hide()">
        Cancel
      </b-button>
    </div>
  </b-modal>
</template>

<script>
import {
  BModal,
  VBModal,
  BForm,
  BFormGroup,
  BFormSelect,
  BButton,
  BRow,
  BCol,
  BSpinner,
  BAlert
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import flatPickr from 'vue-flatpickr-component'
import enhancedInvoiceExportService from '@/services/enhanced-invoice-export-service'
import invoiceExportFormatter from '@/utils/invoice-export-formatter'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default {
  name: 'InvoiceReconciliationExport',
  components: {
    BModal,
    VBModal,
    BForm,
    BFormGroup,
    BFormSelect,
    BButton,
    BRow,
    BCol,
    BSpinner,
    BAlert,
    vSelect,
    flatPickr,
    ToastificationContent
  },
  directives: {
    'b-modal': VBModal
  },
  props: {
    filters: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      loading: false,
      submitError: '',
      exportData: {
        type: null,
        format: 'excel',
        startDate: null,
        endDate: null,
        status: null,
        client: null,
        category: null,
        bookingStatus: null
      },
      exportTypeOptions: [
        { text: 'Select Export Type', value: null },
        { text: 'Reconciliation', value: 'reconciliation' },
        { text: 'Compliance', value: 'compliance' },
        { text: 'Financial', value: 'financial' },
        { text: 'Tax', value: 'tax' },
        { text: 'Booking Status', value: 'booking-status' },
        { text: 'Custom Report', value: 'custom' }
      ],
      formatOptions: [
        { text: 'Excel', value: 'excel' },
        { text: 'CSV', value: 'csv' },
        { text: 'PDF', value: 'pdf' },
        { text: 'JSON', value: 'json' }
      ],
      statusOptions: [
        { title: 'Raised' },
        { title: 'Paid' },
        { title: 'Unpaid' },
        { title: 'Early Attrition' },
        { title: 'Cancelled' }
      ],
      clientOptions: [],
      categoryOptions: [
        { title: 'Recruitment' },
        { title: 'Training' },
        { title: 'Anti Fraud' },
        { title: 'Contractual' },
        { title: 'Payroll' },
        { title: 'BGV' },
        { title: 'Miscellanous' }
      ],
      bookingStatusOptions: [
        { text: 'All', value: null },
        { text: 'Booked', value: 'Booked' },
        { text: 'Not Booked', value: 'Not Booked' },
        { text: 'Pending', value: 'Pending' }
      ],
      error: {
        type: '',
        format: ''
      }
    }
  },
  computed: {
    isFormValid() {
      return !!(this.exportData.type && this.exportData.format)
    }
  },
  watch: {
    filters: {
      immediate: true,
      handler(newFilters) {
        if (newFilters) {
          this.exportData = {
            ...this.exportData,
            ...newFilters
          }
        }
      }
    }
  },
  async created() {
    await this.loadClients()
  },
  methods: {
    async loadClients() {
      try {
        const clientService = await import('@/services/client-service')
        const res = await clientService.default.getClients()
        if (res && res.data) {
          this.clientOptions = res.data
          console.log('[InvoiceReconciliationExport] Clients loaded', { count: res.data.length })
        }
      } catch (error) {
        console.error('[InvoiceReconciliationExport] Error loading clients:', error)
      }
    },
    async performExport() {
      console.log('[InvoiceReconciliationExport] performExport called', { type: this.exportData.type, format: this.exportData.format })
      if (!this.isFormValid) {
        this.validateForm()
        console.log('[InvoiceReconciliationExport] Form invalid, validation failed')
        return
      }

      this.loading = true
      this.submitError = ''

      const filters = {
        startDate: this.exportData.startDate,
        endDate: this.exportData.endDate,
        status: this.exportData.status,
        client: this.exportData.client,
        category: this.exportData.category,
        bookingStatus: this.exportData.bookingStatus
      }

      try {
        let res
        const filename = invoiceExportFormatter.generateFilename(
          this.exportData.type,
          filters
        )

        switch (this.exportData.type) {
          case 'reconciliation':
            res = await enhancedInvoiceExportService.exportReconciliationData(
              filters,
              this.exportData.format
            )
            break
          case 'compliance':
            res = await enhancedInvoiceExportService.exportComplianceData(
              filters,
              this.exportData.format
            )
            break
          case 'financial':
            res = await enhancedInvoiceExportService.exportFinancialData(
              filters,
              this.exportData.format
            )
            break
          case 'tax':
            res = await enhancedInvoiceExportService.exportTaxData(
              filters,
              this.exportData.format
            )
            break
          case 'booking-status':
            res = await enhancedInvoiceExportService.exportBookingStatusReport(
              filters,
              this.exportData.format
            )
            break
          default:
            this.submitError = 'Invalid export type'
            this.loading = false
            return
        }

        if (res) {
          console.log('[InvoiceReconciliationExport] Export successful', { type: this.exportData.type, format: this.exportData.format })
          if (this.exportData.format === 'json') {
            // For JSON, download as file
            const blob = new Blob([JSON.stringify(res, null, 2)], {
              type: 'application/json'
            })
            enhancedInvoiceExportService.downloadFile(blob, `${filename}.json`)
          } else {
            // For Excel, CSV, PDF - download blob directly
            const extension = this.exportData.format === 'excel' ? 'xlsx' : this.exportData.format
            enhancedInvoiceExportService.downloadFile(res, `${filename}.${extension}`)
          }

          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Success',
              text: 'Export completed successfully!',
              icon: 'CheckCircleIcon',
              variant: 'success'
            }
          })
          this.$refs.exportModal.hide()
        } else {
          this.submitError = 'Error exporting data'
        }
      } catch (error) {
        console.error('[InvoiceReconciliationExport] Error exporting data:', error)
        this.submitError = 'Error exporting data. Please try again.'
      } finally {
        this.loading = false
      }
    },
    validateForm() {
      this.error = {
        type: '',
        format: ''
      }

      if (!this.exportData.type) {
        this.error.type = 'Export type is required'
      }
      if (!this.exportData.format) {
        this.error.format = 'Format is required'
      }
    },
    resetModal() {
      this.exportData = {
        type: null,
        format: 'excel',
        startDate: null,
        endDate: null,
        status: null,
        client: null,
        category: null,
        bookingStatus: null
      }
      this.error = {
        type: '',
        format: ''
      }
      this.submitError = ''
      this.loading = false
    },
    show() {
      if (this.filters) {
        this.exportData = {
          ...this.exportData,
          ...this.filters
        }
      }
      this.$refs.exportModal.show()
    },
    hide() {
      this.$refs.exportModal.hide()
    }
  }
}
</script>
